import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { useCallback, useMemo } from 'react'

type MapStyle = 'street' | 'satellite'

const MAP_IMAGE_PATHS: Record<MapStyle, string> = {
  street: '3d_tiles/street-style.jpg',
  satellite: '3d_tiles/satellite-style.jpg',
}

interface UseMapImageCacheResult {
  /** Signed URL for the cached image, or undefined if not cached */
  cachedImageUrl: string | undefined
  /** Whether the cache check is still loading */
  isLoading: boolean
  /** Upload an image blob to the cache */
  uploadImageToCache: (imageBlob: Blob, style: MapStyle) => Promise<void>
  /** Force refresh the cache status */
  forceRefresh: () => Promise<void>
}

/**
 * Hook to manage map image caching in rolodex storage.
 * Images are stored under 3d_tiles/street-style.jpg and 3d_tiles/satellite-style.jpg
 */
export function useMapImageCache(
  kestrelModelId: string | undefined,
  mapStyle: MapStyle
): UseMapImageCacheResult {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()

  const filePath = MAP_IMAGE_PATHS[mapStyle]

  const {
    fileRefs,
    isLoading: isListLoading,
    forceRefresh: refreshFileList,
  } = useRolodexFileLS(
    kestrelModelId
      ? {
          entry_id: kestrelModelId,
          path: filePath,
        }
      : undefined
  )

  const fileQuery = useMemo(() => {
    if (!kestrelModelId || fileRefs.length === 0) {
      return undefined
    }
    return {
      entry_id: kestrelModelId,
      path: filePath,
    }
  }, [kestrelModelId, fileRefs.length, filePath])

  const {
    url: cachedImageUrl,
    isLoading: isUrlLoading,
    forceRefresh: refreshUrl,
  } = useRolodexSignedURLs(fileQuery)

  const isLoading = isListLoading || isUrlLoading

  const uploadImageToCache = useCallback(
    async (imageBlob: Blob, style: MapStyle) => {
      if (!kestrelModelId) {
        console.warn(
          '[MapImageCache] Cannot upload: no kestrelModelId provided'
        )
        return
      }

      const targetPath = MAP_IMAGE_PATHS[style]
      const encodedPath = encodeURIComponent(targetPath)
      const uploadUrlEndpoint = `${ROLODEX_URL}/api/v2/files/${kestrelModelId}/${encodedPath}?content_type=application/octet-stream`

      try {
        const signedUrlResponse = await authorizedAxios.put(
          uploadUrlEndpoint,
          undefined,
          {
            headers: {
              'Content-Type': 'application/octet-stream',
            },
          }
        )
        const signedUrl = signedUrlResponse.data as string

        await authorizedAxios.put(signedUrl, imageBlob, {
          headers: {
            'Content-Type': 'application/octet-stream',
          },
        })
      } catch (error) {
        console.error(
          `[MapImageCache] Failed to cache ${style} map image:`,
          error
        )
        throw error
      }
    },
    [kestrelModelId, ROLODEX_URL, authorizedAxios]
  )

  const forceRefresh = useCallback(async () => {
    await refreshFileList()
    await refreshUrl()
  }, [refreshFileList, refreshUrl])

  return {
    cachedImageUrl,
    isLoading,
    uploadImageToCache,
    forceRefresh,
  }
}
